import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { apiGet } from 'services';

import { Loading, Modal, StringHelpers } from 'e4shared';

import Chart from 'chart.js';

let metricsBreakdownChart = null;

export const MetricsBreakdown = ({ profile, job, show, range, offset, close }) => {
    const colours = [
        { name: 'Blue', backgroundColor: 'rgba(33, 33, 255, 0.2)' }, // blue
        { name: 'Green', backgroundColor: 'rgba(99, 255, 132, 0.2)' }, // green
        { name: 'Orange', backgroundColor: 'rgba(255, 128, 0, 0.2)' }, // orange
        { name: 'Purple', backgroundColor: 'rgba(99, 0, 132, 0.2)' }, // purple
        { name: 'Red', backgroundColor: 'rgba(255, 33, 33, 0.2)' }, // red
        { name: 'Yellow', backgroundColor: 'rgba(255, 255, 0, 0.2)' }, // yellow
    ];

    const guid = useSelector(state => (state.user.user ? state.user.user.guid : ''));
    const currencySymbol = useSelector(state => (state.user.user ? state.user.user.currencySymbol : ''));

    const chartContainer = useRef(null);
    const [data, setData] = useState([]);
    const [field, setField] = useState(0);
    const [loading, setLoading] = useState(true);

    React.useEffect(() => {
        setField(0);
    }, [job, range, offset]);

    React.useEffect(() => {
        if (job?.id) {
            setLoading(true);
            const fieldId = job?.extraFields && job.extraFields.length > 0 ? job.extraFields[field].id : 0;
            const url =
                job?.type === 'CRM'
                    ? `crm-breakdown/${profile.id}/${job.id}/${fieldId}/${range}/${offset}`
                    : `integration-breakdown/${profile.id}/${job.id}/${fieldId}/${range}/${offset}`;
            profile &&
                job &&
                apiGet(url, guid, response => {
                    setData(response);
                    setLoading(false);
                });
        }
    }, [profile, job, range, offset, field, guid]);

    const total = data ? data.reduce((c, d) => c + (job?.calc_Sum ? d.amount : d.count), 0) : 0;

    const chartData = !job
        ? {}
        : {
              id: 'canvas',
              type: 'pie',
              data: {
                  datasets: [
                      {
                          data: data
                              .sort((a, b) => {
                                  const aVal = job.calc_Sum ? a.amount : a.count;
                                  const bVal = job.calc_Sum ? b.amount : b.count;
                                  return aVal < bVal ? 1 : -1;
                              })
                              .map(d => (job.calc_Sum ? d.amount : d.count)),
                          backgroundColor: data.map((d, i) => colours[i % colours.length].backgroundColor),
                      },
                  ],
                  labels: data.map(d => (d.value ? d.value : 'Not set')),
              },
              options: {
                  legend: {
                      position: 'right',
                  },
                  tooltips: {
                      callbacks: {
                          label: function (tooltipItem, data) {
                              const value = data.datasets[0].data[tooltipItem.index];
                              return `${data.labels[tooltipItem.index]} - ${
                                  job.calc_Sum ? StringHelpers.formatMetric('M', value, currencySymbol) : value
                              } - ${
                                  total === 0 ? 0 : ((data.datasets[0].data[tooltipItem.index] / total) * 100).toFixed(0)
                              }%`;
                          },
                      },
                  },
              },
          };

    React.useEffect(() => {
        if (chartContainer?.current) {
            metricsBreakdownChart?.destroy();
            metricsBreakdownChart = new Chart(chartContainer.current, chartData);
        }
    }, [data, chartContainer, chartData]);

    const moveField = direction => {
        let newField = field + direction;
        if (newField < 0) newField = job.extraFields.length - 1;
        if (newField >= job.extraFields.length) newField = 0;
        setField(newField);
    };

    const footer = job ? (
        <div className="flex flex-center">
            <div className="flex-grow">
                {job.extraFields && job.extraFields.length > 1 ? (
                    <div className="pull-left">
                        <button className="btn btn-sm mr10" onClick={() => moveField(-1)}>
                            <i className="fa fa-arrow-left"></i>
                        </button>
                        {job.extraFields[field].name}
                        <button className="btn btn-sm ml10" onClick={() => moveField(1)}>
                            <i className="fa fa-arrow-right"></i>
                        </button>
                    </div>
                ) : undefined}
            </div>
            <button data-testid="button-breakdown-close" className="btn" onClick={() => close()}>
                Close
            </button>
        </div>
    ) : null;

    const title = job
        ? `Breakdown of ${job.name} by ${job.extraFields[field].name} for ${offset === 1 ? 'last' : 'this'} ${range}`
        : '';

    return (
        <Modal show={show} close={close} footer={footer} title={title}>
            {!loading ? (
                data && data.length > 0 ? (
                    <canvas ref={chartContainer} width="100%" height="50%" />
                ) : (
                    <div>Sorry, no data is available for this {range}</div>
                )
            ) : (
                <Loading />
            )}
        </Modal>
    );
};

export default MetricsBreakdown;
